/**
 * ThemeContext - Canvas theme switcher context
 *
 * Provides theme state for switching between:
 * - 'modern' - Current default design
 * - 'macos-classic' - Classic Mac OS 9 / early OS X style
 */
import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

// ============================================================================
// TYPES
// ============================================================================

export type CanvasTheme = 'modern' | 'macos-classic';

interface ThemeContextValue {
  theme: CanvasTheme;
  setTheme: (theme: CanvasTheme) => void;
  toggleTheme: () => void;
}

// ============================================================================
// CONTEXT
// ============================================================================

const ThemeContext = createContext<ThemeContextValue | null>(null);

// ============================================================================
// PROVIDER
// ============================================================================

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: CanvasTheme;
}

export function ThemeProvider({ children, defaultTheme = 'modern' }: ThemeProviderProps) {
  const [theme, setTheme] = useState<CanvasTheme>(defaultTheme);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => (prev === 'modern' ? 'macos-classic' : 'modern'));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
